import type { ProgressSummary } from '../../lib/dashboard/progress';

export interface ChangeWording {
    headline: string;
    detail: string;
    /** The share-of-loss-from-fat bar only means something for a loss. */
    showQualityBar: boolean;
}

const kg = (n: number) => Math.abs(n).toFixed(1);

/**
 * The sentence at the top of Change quality, for every direction of change.
 *
 * It had one shape for a loss and one for anything else, and always added
 * "N % of the deficit came from fat mass". A client who had lost 14.2 kg of
 * fat and gained 17.2 kg of lean read "3.0 kg gained since start" over "0 %
 * of the deficit came from fat mass": there was no deficit, and the best kind
 * of change read as a failure. Fat down with lean up is recomposition, and is
 * said as such whichever way the scale moved.
 */
export const describeChange = (
    s: Pick<ProgressSummary, 'fatKgDelta' | 'leanKgDelta' | 'weightKgDelta' | 'changeQuality'>,
): ChangeWording => {
    const { fatKgDelta: fat, leanKgDelta: lean, weightKgDelta: weight } = s;

    if (fat < 0 && lean > 0) {
        return {
            headline: `${kg(fat)} kg fat lost, ${kg(lean)} kg lean gained`,
            detail:
                weight > 0
                    ? `Weight up ${kg(weight)} kg — fat down, lean up`
                    : weight < 0
                      ? `Weight down ${kg(weight)} kg — fat down, lean up`
                      : 'Weight unchanged — fat down, lean up',
            showQualityBar: false,
        };
    }
    if (weight < 0) {
        const pct = Math.round(s.changeQuality * 100);
        return {
            headline: `${kg(weight)} kg lost — ${s.changeQuality >= 0.999 ? 'all of it fat' : `${pct} % of it fat`}`,
            detail: `${pct} % of the deficit came from fat mass`,
            showQualityBar: true,
        };
    }
    if (weight > 0) {
        const leanShare = lean > 0 ? Math.min(1, lean / weight) : 0;
        return {
            headline:
                leanShare > 0
                    ? `${kg(weight)} kg gained — ${Math.round(leanShare * 100)} % of it lean`
                    : `${kg(weight)} kg gained, all of it fat`,
            detail: `Fat ${fat >= 0 ? '+' : '−'}${kg(fat)} kg, lean ${lean >= 0 ? '+' : '−'}${kg(lean)} kg`,
            showQualityBar: false,
        };
    }
    return { headline: 'Weight unchanged since start', detail: 'No change in fat or lean mass', showQualityBar: false };
};
